import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api/client';
import type {
  CertificadoDoItem,
  Etiqueta,
  EventoDaLinha,
  LinhaDeItem,
  MarcacaoDoItem,
  VerificacaoDaEtiqueta,
} from '../lib/api/types';
import { useAuth } from '../lib/auth/AuthContext';
import { PageHeader } from '../components/ui/PageHeader';
import { Carregando, Erro } from '../components/ui/Estados';
import { emDataHora, hashCurto } from '../lib/format/datetime';

/**
 * O item e a sua linha do tempo (A14) — <b>a tela onde o produto se explica sozinho</b>.
 *
 * <h3>Ela mostra o hash, e o passaporte não</h3>
 * Não é inconsistência: são públicos opostos. Aqui quem lê é a pessoa que vai auditar,
 * conferir uma prova ou responder a um incidente, e esconder o `event_hash` dela seria
 * pedir que confiasse na nossa palavra — que é exatamente o que o protocolo existe para
 * não precisar.
 *
 * <h3>As duas datas aparecem juntas</h3>
 * `eventTime` é declarada pelo aparelho; `recordTime` é carimbada pelo servidor. Quando
 * divergem, a diferença é informação — aparelho que sincronizou dias depois, evento
 * retroativo (ADR-018) — e mostrar uma só esconderia justamente o caso interessante.
 */
export function ItemPage() {
  const { itemId = '' } = useParams();
  const { traduzir, temPapel } = useAuth();
  const clienteDeConsulta = useQueryClient();

  const item = useQuery({
    queryKey: ['item', itemId],
    queryFn: () => api.get<LinhaDeItem>(`/console/items/${itemId}`),
  });
  const eventos = useQuery({
    queryKey: ['item', itemId, 'eventos'],
    queryFn: () => api.get<EventoDaLinha[]>(`/console/items/${itemId}/events`),
  });
  const marcacoes = useQuery({
    queryKey: ['item', itemId, 'marcacoes'],
    queryFn: () => api.get<MarcacaoDoItem[]>(`/console/items/${itemId}/markings`),
  });
  const etiquetas = useQuery({
    queryKey: ['item', itemId, 'etiquetas'],
    queryFn: () => api.get<Etiqueta[]>(`/console/items/${itemId}/tags`),
  });
  // A trilha diz quando um consumidor tocou o produto: mesmos papéis das leituras de consumidor.
  const podeVerTrilha = temPapel('COMPANY_ADMIN', 'COMPANY_SECURITY', 'COMPANY_ANALYST');
  const verificacoes = useQuery({
    queryKey: ['item', itemId, 'verificacoes'],
    queryFn: () =>
      api.get<VerificacaoDaEtiqueta[]>(`/console/items/${itemId}/tag-verifications`),
    enabled: podeVerTrilha && (etiquetas.data ?? []).length > 0,
  });

  const certificados = useQuery({
    queryKey: ['item', itemId, 'certificados'],
    queryFn: () => api.get<CertificadoDoItem[]>(`/console/items/${itemId}/certificates`),
  });
  const [confirmandoEmissao, setConfirmandoEmissao] = useState(false);
  const [revogando, setRevogando] = useState<string | null>(null);
  const [motivoDaRevogacao, setMotivoDaRevogacao] = useState('');

  const emitir = useMutation({
    mutationFn: () => api.post(`/console/items/${itemId}/certificate`),
    onSuccess: () => {
      setConfirmandoEmissao(false);
      void clienteDeConsulta.invalidateQueries({ queryKey: ['item', itemId, 'certificados'] });
    },
  });

  const revogar = useMutation({
    mutationFn: (id: string) =>
      api.post(`/console/certificates/${id}/revoke`, { reason: motivoDaRevogacao }),
    onSuccess: () => {
      setRevogando(null);
      setMotivoDaRevogacao('');
      void clienteDeConsulta.invalidateQueries({ queryKey: ['item', itemId, 'certificados'] });
    },
  });

  const [marcacao, setMarcacao] = useState('STOLEN');
  const [motivo, setMotivo] = useState('');
  const [revogarJunto, setRevogarJunto] = useState(false);
  const certificadosAtivos = (certificados.data ?? []).filter((c) => !c.revogadoEm).length;

  const marcar = useMutation({
    mutationFn: () =>
      api.post(`/console/items/${itemId}/markings`, {
        marking: marcacao,
        reason: motivo,
        revokeCertificates: revogarJunto && certificadosAtivos > 0,
      }),
    onSuccess: () => {
      setMotivo('');
      setRevogarJunto(false);
      void clienteDeConsulta.invalidateQueries({ queryKey: ['item', itemId] });
    },
  });

  const desmarcar = useMutation({
    mutationFn: (m: string) => api.post(`/console/items/${itemId}/markings/${m}/clear`),
    onSuccess: () => void clienteDeConsulta.invalidateQueries({ queryKey: ['item', itemId] }),
  });

  if (item.isPending) {
    return <Carregando o="o item" />;
  }
  if (item.isError) {
    return <Erro erro={item.error} />;
  }

  const i = item.data;
  const ativas = (marcacoes.data ?? []).filter((m) => m.active);

  return (
    <>
      <PageHeader
        rastro={['Itens', i.serial]}
        titulo={i.produto}
        subtitulo={`${i.marca} · lote ${i.lote}`}
      />
      <div className="wrap">
        {ativas.length > 0 && (
          <div className="aviso ruim" style={{ marginBottom: 16 }}>
            <strong>Este item está marcado.</strong>{' '}
            {ativas.map((m) => traduzir('marking', m.marking)).join(', ')} — o passaporte
            público mostra o aviso e deixa de ser cacheado (ADR-063).
          </div>
        )}

        <div className="duas">
          <div className="card cd">
            <div className="cd2-t">Linha do tempo</div>
            {eventos.isPending && <Carregando />}
            {eventos.isError && <Erro erro={eventos.error} />}
            {eventos.data && eventos.data.length === 0 && (
              <div className="t-small">
                Nenhum evento ainda. O item existe, e nenhuma leitura de operador o alcançou —
                que é o sinal de etiqueta que saiu da gráfica e não foi aplicada.
              </div>
            )}
            {eventos.data && eventos.data.length > 0 && (
              <ol className="linhadotempo">
                {eventos.data.map((e) => (
                  <li key={e.id} className={e.bizStep === 'anomaly_detected' ? 'anomalia' : ''}>
                    <div className="passo">{traduzir('bizStep', e.bizStep)}</div>
                    <div className="quando">
                      {emDataHora(e.recordTime)}
                      {e.eventTime !== e.recordTime && (
                        <> · declarado para {emDataHora(e.eventTime)}</>
                      )}
                    </div>
                    <div className="detalhe">
                      {e.readPoint ?? e.geohash ?? 'sem lugar registrado'}
                      {e.assinado ? ' · assinado pelo elo' : ' · SEM contra-assinatura'}
                    </div>
                    <div className="hash">
                      {hashCurto(e.eventHash, 10)}
                      {e.parents.length > 0 && (
                        <> · pais: {e.parents.map((p) => hashCurto(p, 6)).join(', ')}</>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>

          <div style={{ display: 'grid', gap: 16 }}>
            <div className="card cd">
              <div className="cd2-t">Situação</div>
              <div className="kv">
                <span className="k">Posição</span>
                <span className="v">{traduzir('position', i.posicao)}</span>
              </div>
              <div className="kv">
                <span className="k">Nível de garantia</span>
                <span className="v">{i.nivel}</span>
              </div>
              <div className="kv">
                <span className="k">Código</span>
                <span className="v mono-s">{i.serial}</span>
              </div>
              <div className="kv">
                <span className="k">Última leitura</span>
                <span className="v">{emDataHora(i.ultimoEventoEm)}</span>
              </div>
            </div>

            <div className="card cd">
              <div className="cd2-t">Etiquetas</div>
              {(etiquetas.data ?? []).length === 0 && (
                <div className="t-small">
                  Só o código impresso. Sem etiqueta eletrônica, este item é L1 — o código
                  pode ser copiado.
                </div>
              )}
              {(etiquetas.data ?? []).map((t) => (
                <div key={t.id}>
                  <div className="kv">
                    <span className="k">
                      {t.tipo} · {t.nivel}
                    </span>
                    <span className="v mono-s">{t.uid ?? t.status}</span>
                  </div>
                  {/* O lacre só vem de toque verificado (ADR-123): o estado está dentro do CMAC. */}
                  {t.lacre && (
                    <div className="kv">
                      <span className="k">
                        Lacre da embalagem
                        <br />
                        <span className="t-cap">
                          {t.lacre === 'OPEN' && t.lacreAbertoDesde
                            ? `visto aberto pela primeira vez em ${emDataHora(t.lacreAbertoDesde)}`
                            : `conferido em ${emDataHora(t.lacreConferidoEm)}`}
                        </span>
                      </span>
                      <span className="v">
                        <span className={t.lacre === 'OPEN' ? 'chip chip-danger' : 'chip chip-teal'}>
                          {traduzir('seal', t.lacre)}
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {podeVerTrilha && (etiquetas.data ?? []).length > 0 && (
              <div className="card cd">
                <div className="cd2-t">Toques na etiqueta</div>
                {verificacoes.isError && <Erro erro={verificacoes.error} />}
                {verificacoes.data && verificacoes.data.length === 0 && (
                  <div className="t-small">Ninguém tocou a etiqueta ainda.</div>
                )}
                {(verificacoes.data ?? []).map((v, n) => (
                  <div className="kv" key={`${v.quando}-${n}`}>
                    <span className="k">
                      {traduzir('tagResult', v.resultado)}
                      <br />
                      <span className="t-cap">
                        {traduzir('tagSource', v.origem)} · {emDataHora(v.quando)}
                        {v.contador != null ? ` · toque ${v.contador}` : ''}
                      </span>
                    </span>
                    <span className="v">
                      {v.lacre && (
                        <span className={v.lacre === 'OPEN' ? 'chip chip-danger' : 'chip chip-neutral'}>
                          {traduzir('seal', v.lacre)}
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/*
              Certificados (ADR-127). Emitir é declaração da marca: antes do clique, a tela mostra o
              que o documento vai afirmar. Item marcado não recebe certificado (doc 15), e o botão
              diz por quê em vez de sumir sem explicação.
            */}
            <div className="card cd">
              <div className="cd2-t">Certificados</div>

              {temPapel('COMPANY_ADMIN', 'COMPANY_CATALOG') &&
                (ativas.length > 0 ? (
                  <div className="t-small">
                    Com marcação ativa ({ativas.map((m) => m.label).join(', ')}), o item não recebe
                    certificado.
                  </div>
                ) : i.posicao === 'GENERATED' || i.posicao === 'PROVISIONED' ? (
                  <div className="t-small">
                    O item ainda não foi lido na cadeia; o certificado só existe depois disso.
                  </div>
                ) : !confirmandoEmissao ? (
                  <button className="btn btn-secondary" onClick={() => setConfirmandoEmissao(true)}>
                    Emitir certificado
                  </button>
                ) : (
                  <div className="aviso">
                    <div>
                      Será emitido um{' '}
                      <strong>
                        {i.nivel === 'L1'
                          ? 'Certificado de Rastreabilidade'
                          : 'Certificado de Autenticidade e Rastreabilidade'}
                      </strong>
                      , com página pública permanente e PDF.
                    </div>
                    <div className="t-small" style={{ marginTop: 6 }}>
                      {i.nivel === 'L1'
                        ? 'Ele registra a trajetória da unidade e diz que não verifica o objeto físico.'
                        : 'Ele afirma que leituras na cadeia verificaram a etiqueta eletrônica — a originalidade da etiqueta, e não o conteúdo da embalagem.'}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <button
                        className="btn btn-primary"
                        disabled={emitir.isPending}
                        onClick={() => emitir.mutate()}
                      >
                        {emitir.isPending ? 'Emitindo…' : 'Emitir'}
                      </button>
                      <button
                        className="btn btn-ghost"
                        disabled={emitir.isPending}
                        onClick={() => setConfirmandoEmissao(false)}
                      >
                        Cancelar
                      </button>
                    </div>
                    {emitir.isError && <Erro erro={emitir.error} />}
                  </div>
                ))}

              {certificados.data && certificados.data.length === 0 && (
                <div className="t-small" style={{ marginTop: 8 }}>
                  Nenhum certificado emitido.
                </div>
              )}
              {(certificados.data ?? []).map((cert) => (
                <div className="kv" key={cert.id}>
                  <span className="k">
                    {cert.nivel} · {emDataHora(cert.emitidoEm)}
                    <br />
                    <span className="t-cap">
                      {cert.revogadoEm
                        ? `Revogado em ${emDataHora(cert.revogadoEm)}: ${cert.motivoDaRevogacao ?? 'sem motivo registrado'}`
                        : cert.titulo}
                    </span>
                    {revogando === cert.id && (
                      <span style={{ display: 'block', marginTop: 8 }}>
                        <input
                          className="txt"
                          style={{ width: '100%' }}
                          placeholder="Por que revogar (fica registrado)"
                          value={motivoDaRevogacao}
                          onChange={(e) => setMotivoDaRevogacao(e.target.value)}
                        />
                        <span className="t-cap" style={{ display: 'block', marginTop: 4 }}>
                          Definitivo: a página pública passa a dizer que foi revogado.
                        </span>
                        <span style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                          <button
                            className="btn btn-primary"
                            disabled={revogar.isPending || motivoDaRevogacao.trim().length < 10}
                            onClick={() => revogar.mutate(cert.id)}
                          >
                            Revogar
                          </button>
                          <button className="btn btn-ghost" onClick={() => setRevogando(null)}>
                            Cancelar
                          </button>
                        </span>
                        {revogar.isError && <Erro erro={revogar.error} />}
                      </span>
                    )}
                  </span>
                  <span className="v" style={{ textAlign: 'right' }}>
                    <a href={cert.url} target="_blank" rel="noreferrer">
                      Página
                    </a>
                    {cert.pdf && (
                      <>
                        {' · '}
                        <a href={cert.pdf} target="_blank" rel="noreferrer">
                          PDF
                        </a>
                      </>
                    )}
                    {!cert.revogadoEm &&
                      revogando !== cert.id &&
                      temPapel('COMPANY_ADMIN', 'COMPANY_SECURITY') && (
                        <>
                          <br />
                          <button
                            className="btn btn-ghost"
                            onClick={() => {
                              setRevogando(cert.id);
                              setMotivoDaRevogacao('');
                            }}
                          >
                            Revogar
                          </button>
                        </>
                      )}
                  </span>
                </div>
              ))}
            </div>

            <div className="card cd">
              <div className="cd2-t">Marcações</div>
              {(marcacoes.data ?? []).length === 0 && (
                <div className="t-small">Nenhuma, nem hoje nem antes.</div>
              )}
              {(marcacoes.data ?? []).map((m) => (
                <div className="kv" key={`${m.marking}-${m.setAt}`}>
                  <span className="k">
                    {m.label}
                    <br />
                    <span className="t-cap">
                      {traduzir('markingSource', m.source)} · {emDataHora(m.setAt)}
                      {m.reason ? ` · ${m.reason}` : ''}
                    </span>
                  </span>
                  <span className="v">
                    {m.active ? (
                      temPapel('COMPANY_SECURITY') ? (
                        <button
                          className="btn btn-ghost"
                          onClick={() => desmarcar.mutate(m.marking)}
                        >
                          Remover
                        </button>
                      ) : (
                        <span className="chip chip-danger">ativa</span>
                      )
                    ) : (
                      <span className="chip chip-neutral">removida</span>
                    )}
                  </span>
                </div>
              ))}

              {temPapel('COMPANY_ADMIN', 'COMPANY_SECURITY') && (
                <form
                  className="form"
                  style={{ marginTop: 16 }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    marcar.mutate();
                  }}
                >
                  <div className="campo">
                    <label htmlFor="m">Marcar este item como</label>
                    <select id="m" value={marcacao} onChange={(e) => setMarcacao(e.target.value)}>
                      {['STOLEN', 'RECALLED', 'BLOCKED', 'IN_SERVICE', 'RETURNED'].map((c) => (
                        <option key={c} value={c}>
                          {traduzir('marking', c)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="campo">
                    <label htmlFor="motivo">Motivo</label>
                    <input
                      id="motivo"
                      value={motivo}
                      onChange={(e) => setMotivo(e.target.value)}
                      required
                    />
                    <span className="dica">
                      Obrigatório. Quem ler a trilha daqui a meses precisa saber por quê.
                    </span>
                  </div>
                  {certificadosAtivos > 0 && (
                    <div className="campo">
                      <label className="checkbox">
                        <input
                          type="checkbox"
                          checked={revogarJunto}
                          onChange={(e) => setRevogarJunto(e.target.checked)}
                        />{' '}
                        Revogar também {certificadosAtivos === 1
                          ? 'o certificado ativo'
                          : `os ${certificadosAtivos} certificados ativos`}
                      </label>
                      <span className="dica">
                        Com o mesmo motivo (de 10 a 300 caracteres). Remover a marcação depois não
                        devolve o certificado.
                      </span>
                    </div>
                  )}
                  {marcar.isError && <div className="aviso ruim">{String(marcar.error)}</div>}
                  <button className="btn btn-danger" type="submit" disabled={marcar.isPending}>
                    Marcar
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
